import { useRef, useState } from "react";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB

interface Props {
  onUpload: (file: File) => Promise<void>;
  onAddUrl?: (url: string) => Promise<void>;
  atLimit?: boolean;
}

export default function UploadZone({ onUpload, onAddUrl, atLimit = false }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragover, setDragover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [ytUrl, setYtUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    if (files[0].size > MAX_UPLOAD_BYTES) {
      setError("File exceeds the 10 MB limit.");
      return;
    }
    setUploading(true);
    try {
      await onUpload(files[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleAddUrl = async () => {
    const url = ytUrl.trim();
    if (!url || !onAddUrl) return;
    setError(null);
    setAdding(true);
    try {
      await onAddUrl(url);
      setYtUrl("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add video.");
    } finally {
      setAdding(false);
    }
  };

  if (atLimit) {
    return (
      <p className="text-sm text-text-tertiary text-center mt-4">
        Library full (3 items max). Delete an item to add a new one.
      </p>
    );
  }

  return (
    <div>
      <label
        className={`block border-2 border-dashed rounded-[14px] px-8 py-12 text-center cursor-pointer transition-colors duration-200 text-sm ${
          dragover
            ? "border-accent bg-accent-soft text-accent-deep"
            : "border-border text-text-tertiary hover:border-accent/60 hover:text-text-secondary"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
        onDragLeave={() => setDragover(false)}
        onDrop={(e) => { e.preventDefault(); setDragover(false); void handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".md,.txt"
          onChange={(e) => void handleFiles(e.target.files)}
          className="hidden"
        />
        {uploading ? "Uploading…" : "Drop a .md or .txt file here, or click to browse (max 10 MB)"}
      </label>
      {onAddUrl && (
        <div className="flex gap-2 mt-4">
          <input
            type="url"
            value={ytUrl}
            onChange={(e) => setYtUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void handleAddUrl(); }}
            placeholder="Paste a YouTube URL…"
            className="input-base flex-1 rounded-full px-4"
            disabled={adding}
          />
          <button
            onClick={() => void handleAddUrl()}
            disabled={adding || !ytUrl.trim()}
            className="btn-quiet flex-shrink-0"
          >
            {adding ? "Adding…" : "Add"}
          </button>
        </div>
      )}
      {error && <p className="text-sm mt-3 font-medium text-error">{error}</p>}
    </div>
  );
}
